import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ALL_SCREENS, getScreen, shuffleOptions } from '../../lib/onboardingProfile';
import { useLocale } from '../LocaleProvider';

const QUESTIONS_ES = {
  motivo: '¿Qué te trae hoy a MiniMental?', segmento: '¿Quién eres?', contexto: {
    medico: '¿Dónde trabajas principalmente?', residente: '¿Cuál es tu especialidad de residencia?', estudiante: '¿En qué etapa de la carrera estás?', otro_profissional: '¿Cuál es tu área?'
  }, necesidad: '¿Qué te ayudaría más hoy?', amostra: '¿Quieres recibir por correo una muestra gratuita del Kit del Plantonista?'
};
const OPTIONS_ES = {
  calcular_escala: 'Calcular una escala o puntuación', consultar_referencia: 'Consultar una referencia', conhecer_o_site: 'Conocer el sitio', outro: 'Otro', outra: 'Otra',
  medico: 'Médico/a', residente: 'Residente', estudante: 'Estudiante de medicina', outro_profissional: 'Otro profesional de salud',
  plantao: 'Guardia o urgencias', consultorio: 'Consultorio o consulta externa', hospital: 'Sala o hospital', clinica: 'Medicina interna', pediatria: 'Pediatría', cirurgia: 'Cirugía', emergencia: 'Urgencias', go: 'Ginecología y obstetricia', ciclo_basico: 'Ciclo básico', ciclo_clinico: 'Ciclo clínico', internato: 'Internado', preparando_residencia: 'Preparación para la residencia', enfermagem: 'Enfermería', farmacia: 'Farmacia',
  conduta: 'Decisiones clínicas y prescripción rápida', calculadoras: 'Calculadoras y escalas', presenca_digital: 'Presencia digital y captación de pacientes', aprender_conduta: 'Aprender decisiones clínicas con seguridad', prova_residencia: 'Examen de residencia',
  sim: 'Sí, quiero recibirla', nao: 'Ahora no'
};

// Perguntas do perfil. O tempo de cada tela só corre com a aba visível; a posição clicada (na ordem exibida)
// serve para detectar clique em sequência. Nada aqui coleta dado de paciente ou resultado de cálculo.
function visibleNow() {
  return typeof document === 'undefined' || document.visibilityState === 'visible';
}

export default function ProfileSteps({ seed, onComplete, onSkip }) {
  const { locale } = useLocale();
  const es = locale === 'es';
  const [answers, setAnswers] = useState({});
  const [index, setIndex] = useState(0);
  const timings = useRef({});
  const positions = useRef({});
  const shownAt = useRef(null);

  const screenId = ALL_SCREENS[index];
  const screen = getScreen(screenId, answers);
  const options = useMemo(
    () => (screen ? shuffleOptions(screen.options, `${seed}:${screenId}:${answers.segmento || ''}`) : []),
    [screen, seed, screenId, answers.segmento],
  );

  useEffect(() => {
    const flush = () => {
      if (shownAt.current != null) {
        timings.current[screenId] = (timings.current[screenId] || 0) + (performance.now() - shownAt.current);
        shownAt.current = null;
      }
    };
    const resume = () => { if (visibleNow() && shownAt.current == null) shownAt.current = performance.now(); };
    const onVis = () => (visibleNow() ? resume() : flush());
    resume();
    document.addEventListener('visibilitychange', onVis);
    return () => { flush(); document.removeEventListener('visibilitychange', onVis); };
  }, [screenId]);

  function choose(option, position) {
    positions.current[screenId] = position;
    const next = { ...answers, [screenId]: option.value };
    setAnswers(next);
    if (index === ALL_SCREENS.length - 1) {
      // Última tela: soma o trecho ainda em curso. Nas outras, o efeito da tela fecha o tempo ao trocar de índice
      // (somar aqui também contaria o mesmo trecho duas vezes).
      const elapsed = (timings.current[screenId] || 0) + (shownAt.current != null ? performance.now() - shownAt.current : 0);
      const finalTimings = { ...timings.current, [screenId]: Number.isFinite(elapsed) ? elapsed : 0 };
      onComplete({ answers: next, timings: finalTimings, positions: { ...positions.current } });
    } else {
      setIndex(index + 1);
    }
  }

  if (!screen) return null;
  return (
    <div>
        <p className="eyebrow text-xs">{es ? `Pregunta ${index + 1} de ${ALL_SCREENS.length}` : `Pergunta ${index + 1} de ${ALL_SCREENS.length}`}</p>
      <fieldset className="mt-2">
        <legend className="text-base font-medium text-[var(--text)]">{es ? (typeof QUESTIONS_ES[screenId] === 'object' ? QUESTIONS_ES[screenId][answers.segmento] : QUESTIONS_ES[screenId]) : screen.question}</legend>
        <div className="mt-4 grid gap-2">
          {options.map((o, position) => (
            <button key={o.value} type="button" onClick={() => choose(o, position)} className="btn-secondary w-full text-left">
              {es ? (OPTIONS_ES[o.value] || o.label) : o.label}
            </button>
          ))}
        </div>
      </fieldset>
      <button type="button" onClick={onSkip} className="mt-5 block w-full text-right text-sm text-[var(--text-muted)] underline">{es ? 'Omitir por ahora' : 'Pular por agora'}</button>
    </div>
  );
}
